import { loggableError } from "@/lib/ai-provider/errors";
import { apiFieldErrorMessage } from "@/lib/errors/api-error";
import { intlLocale } from "@/i18n/intl-locale";
import type { Locale } from "@/i18n/types";
import type { IInstanceConfig } from "@/types/instance";

/**
 * Which of the API's two upload limits a file is measured against (NG-3279).
 * A picture (a workspace logo, an avatar, an image in an intake rich-text
 * page) takes the image limit; every other upload (an attachment, a project or
 * template file, an intake file answer) takes the file limit. The split
 * mirrors the backend's `IMAGE_ENTITY_TYPES`.
 */
type UploadKind = "file" | "image";

type UploadLimits = Pick<IInstanceConfig, "file_size_limit" | "image_size_limit">;

/**
 * The most bytes the API accepts for an upload of `kind`, as the instance
 * config publishes it, or `undefined` while the config is not loaded. The API
 * enforces its limit either way, so an unknown one only means the user hears it
 * from the server's answer instead of before the request.
 */
export function uploadLimitFor(
  config: UploadLimits | null | undefined,
  kind: UploadKind,
): number | undefined {
  const limit = kind === "image" ? config?.image_size_limit : config?.file_size_limit;
  return typeof limit === "number" && limit > 0 ? limit : undefined;
}

/** A file refused before any request, because it is over the limit it names. */
export class UploadTooLargeError extends Error {
  constructor(readonly limit: number) {
    super(`File is larger than the ${limit} byte upload limit`);
    this.name = "UploadTooLargeError";
  }
}

/**
 * Throws `UploadTooLargeError` when `file` is over the smallest of `limits`.
 * An unknown limit (`undefined`) is skipped, so a caller passes the API's limit
 * and, where it has one, a smaller one of its own (an intake field's
 * `max_size_mb`).
 */
export function assertUploadSize(
  file: Pick<File, "size">,
  ...limits: readonly (number | undefined)[]
): void {
  const known = limits.filter((limit): limit is number => typeof limit === "number" && limit > 0);
  if (known.length === 0) return;
  const limit = Math.min(...known);
  if (file.size > limit) throw new UploadTooLargeError(limit);
}

/** A file refused before any request, because its type is not in the field's allow-list. */
export class UploadTypeNotAllowedError extends Error {
  constructor(readonly accepted: readonly string[]) {
    super(`File type is not in the accepted list: ${accepted.join(", ")}`);
    this.name = "UploadTypeNotAllowedError";
  }
}

/**
 * Whether `type` (a file's MIME type) matches one of `patterns`, each an exact
 * type (`application/pdf`) or a family glob (`image/*`) — the same glob-aware
 * match the HTML `accept` attribute and the backend's `_mime_type_matches_any`
 * (`cognota/intake/views.py`) use. Case-insensitive; an any-type wildcard
 * (star-slash-star) matches anything.
 */
export function mimeTypeMatches(type: string, patterns: readonly string[]): boolean {
  const candidate = type.trim().toLowerCase();
  if (!candidate) return false;
  return patterns.some((raw) => {
    const pattern = raw.trim().toLowerCase();
    if (!pattern) return false;
    if (pattern === "*/*") return true;
    if (pattern.endsWith("/*")) return candidate.startsWith(pattern.slice(0, -1));
    return candidate === pattern;
  });
}

/**
 * Throws `UploadTypeNotAllowedError` when `file`'s type is not in `accepted`.
 * An empty / undefined list is skipped (the field accepts anything), mirroring
 * `assertUploadSize`. A file the browser gave no type coerces to
 * `application/octet-stream`, the same value the presign request declares, so
 * this refusal matches the server's. The server re-checks at presign + finalize
 * regardless; this only moves the refusal ahead of the request (NG-3864).
 */
export function assertUploadType(
  file: Pick<File, "type">,
  accepted: readonly string[] | undefined,
): void {
  if (!accepted || accepted.length === 0) return;
  const type = file.type || "application/octet-stream";
  if (!mimeTypeMatches(type, accepted)) throw new UploadTypeNotAllowedError(accepted);
}

const KIB = 1024;
const MIB = 1024 ** 2;
const GIB = 1024 ** 3;

/**
 * A limit as people read it, in their language: `10 MB` / `10 Mo`, `1 GB` /
 * `1 Go`. Binary multiples under the familiar names, rounded the way the API's
 * own refusal (`_format_limit` in `core/api/views_asset.py`) and V2 show them,
 * so the message before the request and the one after it name the same number.
 */
export function formatUploadLimit(bytes: number, locale: Locale): string {
  const [value, unit] =
    bytes >= GIB && bytes % GIB === 0
      ? [bytes / GIB, "gigabyte"]
      : bytes >= MIB
        ? [Math.round(bytes / MIB), "megabyte"]
        : bytes >= KIB
          ? [Math.round(bytes / KIB), "kilobyte"]
          : [bytes, "byte"];
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "unit",
    unit,
    unitDisplay: "short",
    maximumFractionDigits: 0,
    // `1536 MB`, as the API writes it, not `1,536 MB`.
    useGrouping: false,
  }).format(value);
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** "This file is larger than the 10 MB limit.", in the user's language. */
export function uploadTooLargeMessage(limit: number, t: Translate, locale: Locale): string {
  return t("uploads.too_large", { limit: formatUploadLimit(limit, locale) });
}

/** "This file type isn't accepted here. Allowed: …", in the user's language.
 *  Internal: only `uploadFailureMessage` surfaces it (unlike the size message,
 *  which callers show at the field). */
function uploadTypeNotAllowedMessage(accepted: readonly string[], t: Translate): string {
  return t("uploads.type_not_allowed", { types: accepted.join(", ") });
}

/**
 * What to tell the user about a failed upload, or `undefined` when there is
 * nothing more specific than the caller's own "couldn't upload".
 *
 * A file over its limit names the limit, in the user's language. An API 400
 * shows the server's own text: a type the upload refuses, an intake field's
 * own limit, or a stored size the server measured over the limit. Storage
 * answers in XML, never the JSON envelope, so a refused PUT or POST to storage
 * has no text here and falls back.
 */
export function uploadFailureMessage(
  error: unknown,
  t: Translate,
  locale: Locale,
): string | undefined {
  if (error instanceof UploadTooLargeError) return uploadTooLargeMessage(error.limit, t, locale);
  if (error instanceof UploadTypeNotAllowedError)
    return uploadTypeNotAllowedMessage(error.accepted, t);
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 400 ? apiFieldErrorMessage(error) : undefined;
}

/**
 * Log an upload that did not happen, at the level it deserves, and with
 * nothing in it that must not leave the browser. Every upload surface logs
 * through this one helper.
 *
 * A file refused before any request (`UploadTooLargeError` for size,
 * `UploadTypeNotAllowedError` for type) is expected, not a failure, so it is a
 * warning with the limit or the accepted list. Anything else is an error with
 * the request's status, code and message.
 *
 * Never the raw error. console.warn and console.error reach Sentry Logs in
 * full, and when the storage upload is what failed, `uploadToPresignedUrl`
 * rethrows its AxiosError, whose config holds the presigned URL (a live write
 * credential signed into the query string) and the signed form fields. The
 * Sentry hook that cuts signed URLs to their origin covers breadcrumbs and
 * spans, not logs. So only `loggableError`'s status, code and message are
 * logged, and not its `url`, because for a storage request that IS the signed
 * URL. `context` carries ids and the size: never the file name, which is
 * customer content.
 */
export function logUploadFailure(
  error: unknown,
  subject: string,
  context: Readonly<Record<string, unknown>>,
): void {
  if (error instanceof UploadTooLargeError) {
    console.warn(`Refused ${subject} over the upload limit`, { ...context, limit: error.limit });
    return;
  }
  if (error instanceof UploadTypeNotAllowedError) {
    console.warn(`Refused ${subject} of a disallowed type`, {
      ...context,
      accepted: error.accepted,
    });
    return;
  }
  const { url: _signedUrl, ...cause } = loggableError(error);
  console.error(`Failed to upload ${subject}`, { ...context, ...cause });
}
